import React from 'react';
import { cn, LEAK_META, severityFor } from './utils';
import { MotifBackground } from './MotifBackground';

/** Card — light + dark variants, optional top accent bar */
export const Card = ({ className = '', accent, dark = false, motif = false, children, ...rest }) => {
  return (
    <div
      className={cn(
        'relative rounded-lg overflow-hidden border transition-shadow duration-200',
        dark
          ? 'bg-navy-surface border-navy-line text-icy shadow-e2'
          : 'bg-[var(--surface)] border-[var(--line)] text-[var(--ink)] shadow-e1 hover:shadow-e2',
        className,
      )}
      {...rest}
    >
      {accent && (
        <span
          className="absolute top-0 left-0 right-0 h-1"
          style={{ background: typeof accent === 'string' && accent !== 'spectrum' ? accent : undefined }}
        >
          {accent === 'spectrum' && <span className="block h-full bg-spectrum" />}
        </span>
      )}
      {motif && dark && (
        <MotifBackground surface="footer" />
      )}
      <div className="relative">{children}</div>
    </div>
  );
};

/** LeakCard — left border in the leak's spectrum colour */
export const LeakCard = ({ leak, title, body, className = '', children }) => {
  const meta = LEAK_META[leak];
  return (
    <div
      className={cn(
        'relative rounded-lg bg-[var(--surface)] border border-[var(--line)] shadow-e1 hover:shadow-e2 transition-shadow p-5',
        className,
      )}
      style={{ borderLeftWidth: 4, borderLeftColor: meta.color }}
    >
      <div className="flex items-center gap-2 mb-2">
        <span className="h-2 w-2 rounded-full" style={{ background: meta.color }} aria-hidden />
        <span className="k-eyebrow text-[var(--muted)]">{meta.name} Leak</span>
      </div>
      {title && <h3 className="k-h3 text-[var(--ink)] mb-1">{title}</h3>}
      {body && <p className="k-body text-[var(--muted)]">{body}</p>}
      {children}
    </div>
  );
};

/** SectionBand — white / tint / navy + optional motif */
export const SectionBand = ({ tone = 'white', motif = false, className = '', children, id }) => {
  const tones = {
    white: 'k-light',
    tint:  'k-light',
    navy:  'k-dark',
  };
  const bg = {
    white: 'bg-white',
    tint:  'bg-[#F5F8FC]',
    navy:  'bg-navy text-icy',
  }[tone];
  return (
    <section id={id} className={cn('relative', tones[tone], bg, 'k-section', className)}>
      {motif && tone === 'navy' && (
        <MotifBackground surface="hero" />
      )}
      <div className="k-container relative">{children}</div>
    </section>
  );
};

/** Badge / SeverityPill / LeakTag */
export const Badge = ({ tone = 'neutral', children, className = '' }) => {
  const tones = {
    neutral: 'bg-[var(--tint)] text-[var(--ink)] border border-[var(--line)]',
    high:    'bg-sev-high/10 text-sev-high border border-sev-high/30',
    mid:     'bg-sev-mid/10 text-sev-mid border border-sev-mid/30',
    low:     'bg-sev-low/10 text-sev-low border border-sev-low/30',
    spectrum:'bg-spectrum text-icy',
  };
  return (
    <span className={cn('inline-flex items-center px-3 h-7 rounded-pill text-[12px] font-semibold font-sans tracking-wide', tones[tone], className)}>
      {children}
    </span>
  );
};

export const SeverityPill = ({ score, className = '' }) => {
  const s = severityFor(score);
  const tone = s.key === 'sealed' ? 'low' : s.key === 'leaking' ? 'mid' : 'high';
  return <Badge tone={tone} className={className}>{s.label}</Badge>;
};

export const LeakTag = ({ leak, className = '' }) => {
  const meta = LEAK_META[leak];
  return (
    <span className={cn('inline-flex items-center gap-2 px-3 h-7 rounded-pill text-[12px] font-semibold bg-[var(--tint)] text-[var(--ink)] border border-[var(--line)]', className)}>
      <span className="h-2 w-2 rounded-full" style={{ background: meta.color }} aria-hidden />
      {meta.name}
    </span>
  );
};
